import { ProductImageRendition } from "@omni/types/api/product/shared/enums/productImageRendition.enum";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { cn } from "@omni/utils/styles";

import { Config } from "@/utils/config.utils";

type ExtendedMedia = Media & {
    media: string;
};

export type Props = {
    lazyLoad?: boolean;
    parentFit?: boolean;
    medias: Array<Media>;
    className?: string;
    altText?: string | null;
    defaultImageUrl?: string;
    rendition?: ProductImageRendition;
};

const Image = ({
    lazyLoad = false,
    parentFit = false,
    medias = [],
    className,
    altText,
    defaultImageUrl,
    rendition
}: Props) => {
    const sortMedias = (medias: Array<Media>) => {
        return medias.sort((a, b) => {
            const aWidth = getWidth(a.format);
            const bWidth = getWidth(b.format);

            if (aWidth < bWidth) return 1;
            if (aWidth > bWidth) return -1;
            return 0;
        });
    };

    const getWidth = (format?: string | null) => {
        if (!format) {
            return 0;
        }
        return parseInt(format.split("x")[0], 10);
    };

    const toSrcSet = (medias: Array<ExtendedMedia>) => {
        return medias.map((m) => `${m.url} ${getWidth(m.format)}w`).join(",");
    };

    const getMinWidth = (format?: string | null) => {
        if (format) {
            const width = getWidth(format);
            return `(min-width:${width}px)`;
        }
        return "(min-width:0px)";
    };

    const calculateMediaValue = (medias: Array<Media> = []) => {
        const sortedMedias = sortMedias(medias);
        return sortedMedias.map<ExtendedMedia>((m, index) => {
            const newIndex = index + 1;
            const previousMedia =
                newIndex < sortedMedias.length
                    ? sortedMedias[newIndex]
                    : undefined;
            return {
                ...m,
                media: getMinWidth(previousMedia?.format)
            };
        });
    };

    let srcSet: ExtendedMedia[] = calculateMediaValue(
        medias.filter((m) => m.format !== ProductImageRendition.ORIGINAL)
    );

    let originalSource = medias.find(
        (m) => m.format === ProductImageRendition.MD
    );

    if (rendition) {
        srcSet = [];
        originalSource = medias.find((m) => m.format === rendition);
        if (parentFit && lazyLoad) {
            srcSet = calculateMediaValue(
                originalSource ? [originalSource] : []
            );
        }
    }

    return (
        <>
            {lazyLoad && (
                <>
                    {!parentFit && (
                        <picture>
                            {srcSet.map((s, index) => (
                                <source
                                    key={index}
                                    srcSet={s.url}
                                    media={s.media}
                                    type={s.mimeType || undefined}
                                    width={146}
                                    height={109}
                                />
                            ))}
                            <img
                                src={
                                    originalSource?.url ||
                                    defaultImageUrl ||
                                    Config.general["fallback-image-path"]
                                }
                                data-sizes={"auto"}
                                className={cn("tw:block tw:h-auto", className)}
                                alt={
                                    altText ||
                                    originalSource?.altText ||
                                    undefined
                                }
                                width={146}
                                height={109}
                                loading={lazyLoad ? "lazy" : "eager"}
                                decoding={lazyLoad ? "async" : "auto"}
                                fetchPriority={lazyLoad ? "auto" : "high"}
                            />
                        </picture>
                    )}
                    {parentFit && (
                        <img
                            src={
                                originalSource?.url ||
                                defaultImageUrl ||
                                Config.general["fallback-image-path"]
                            }
                            data-sizes={"auto"}
                            data-parent-fit={"width"}
                            className={cn("tw:block tw:h-auto", className)}
                            data-srcset={toSrcSet(srcSet)}
                            alt={
                                altText || originalSource?.altText || undefined
                            }
                            loading="lazy"
                            decoding="async"
                            src-set={toSrcSet(srcSet)}
                            width={146}
                            height={109}
                        />
                    )}
                </>
            )}
            {!lazyLoad && (
                <picture>
                    {srcSet.map((s, index) => (
                        <source
                            key={index}
                            srcSet={s.url}
                            media={s.media}
                            type={s.mimeType || undefined}
                            width={146}
                            height={109}
                        />
                    ))}
                    {!originalSource && (
                        <img
                            src={
                                defaultImageUrl ||
                                Config.general["fallback-image-path"]
                            }
                            className={cn("tw:block tw:h-auto", className)}
                            alt={altText || undefined}
                            width={146}
                            height={109}
                        />
                    )}
                    {!!originalSource && (
                        <img
                            src={originalSource.url}
                            className={cn("tw:block tw:h-auto", className)}
                            alt={altText || originalSource.altText || undefined}
                            width={146}
                            height={109}
                        />
                    )}
                </picture>
            )}
        </>
    );
};

export { Image };
